import { useCallback, useEffect, useState } from 'react';

export type FavoriteItem = {
  comicId: string;
  addedAt: number;
};

const STORAGE_KEY = 'douman_favorites';
const EVENT = 'douman-favorites-change';

function readFavorites(): FavoriteItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeFavorites(items: FavoriteItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new Event(EVENT));
  } catch {
    /* ignore storage errors */
  }
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);

  useEffect(() => {
    setFavorites(readFavorites());
    const handler = () => setFavorites(readFavorites());
    window.addEventListener(EVENT, handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener(EVENT, handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const isFavorite = useCallback(
    (comicId: string) => favorites.some((item) => item.comicId === comicId),
    [favorites],
  );

  const toggleFavorite = useCallback((comicId: string) => {
    const current = readFavorites();
    const exists = current.some((item) => item.comicId === comicId);
    const next = exists
      ? current.filter((item) => item.comicId !== comicId)
      : [{ comicId, addedAt: Date.now() }, ...current];
    writeFavorites(next);
    return !exists;
  }, []);

  const removeFavorite = useCallback((comicId: string) => {
    const next = readFavorites().filter((item) => item.comicId !== comicId);
    writeFavorites(next);
  }, []);

  const clearFavorites = useCallback(() => {
    writeFavorites([]);
  }, []);

  return { favorites, isFavorite, toggleFavorite, removeFavorite, clearFavorites };
}